import { Grid } from '@farewill/ui'
import { useFormikContext } from 'formik'

import InputFloating from 'components/form/input-floating'
import DateSplit from 'components/form/date-split'
import { splitIsoDate } from 'components/form/helpers'
import { TITLE_OPTIONS } from 'utils/enums'

import withForm from 'lib/formik/with-form'
import useValidatedSave from 'lib/formik/use-validated-save'
import { DECEASED_SCHEMA } from 'lib/probate/estate-questionnaire-schema'
import { ProbateEstate } from 'lib/models/probate-estate'
import { ProbateCase } from 'lib/models/probate-case'
import { Address as AddressType } from 'lib/models/address'

import Section from '../components/section'
import Address from '../components/address'
import {
  FieldRow,
  FieldRowItem,
  TitleFieldRowItem,
} from '../components/field-row'

const NAME_ATTRIBUTES: Record<string, string> = {
  firstName: 'deceasedFirstName',
  lastName: 'deceasedLastName',
}

type HandleSave = (event: { name: string; value: unknown }) => void

type Props = {
  probateEstate: ProbateEstate
  probateCase: ProbateCase
  saveProbateEstateField: HandleSave
  saveProbateCaseField: HandleSave
}

const DeceasedFields = ({
  saveProbateEstateField,
  saveProbateCaseField,
}: Pick<Props, 'saveProbateEstateField' | 'saveProbateCaseField'>) => {
  const { values } = useFormikContext<Record<string, unknown>>()

  const handleSaveEstateField = useValidatedSave({
    schema: DECEASED_SCHEMA,
    save: saveProbateEstateField,
  })

  const handleSaveName = useValidatedSave({
    schema: DECEASED_SCHEMA,
    save: ({ name, value }) =>
      saveProbateCaseField({ name: NAME_ATTRIBUTES[name], value }),
  })

  return (
    <Section
      heading="1.1 The deceased"
      notesName="deceasedNotes"
      onNotesSave={handleSaveEstateField}
    >
      <Grid.Item span={12}>
        <FieldRow>
          <TitleFieldRowItem>
            <InputFloating
              name="title"
              label="Title"
              component="select"
              options={TITLE_OPTIONS}
              handleSave={handleSaveEstateField}
            />
          </TitleFieldRowItem>
          <FieldRowItem>
            <InputFloating
              name="firstName"
              label="First names"
              handleSave={handleSaveName}
            />
          </FieldRowItem>
          <FieldRowItem>
            <InputFloating
              name="lastName"
              label="Last name"
              handleSave={handleSaveName}
            />
          </FieldRowItem>
        </FieldRow>
      </Grid.Item>
      <Grid.Item span={12}>
        <InputFloating
          name="nationalInsuranceNumber"
          label="National insurance number"
          handleSave={handleSaveEstateField}
        />
      </Grid.Item>
      <Grid.Item span={12}>
        <Address
          label="Address"
          name="address"
          data={(values.address as AddressType) ?? null}
        />
      </Grid.Item>
      <Grid.Item span={12}>
        <DateSplit
          composeParts
          name="dateOfBirth"
          label="Date of birth"
          handleSave={handleSaveEstateField}
        />
      </Grid.Item>
      <Grid.Item span={12}>
        <DateSplit
          composeParts
          name="dateOfDeath"
          label="Date of death"
          handleSave={handleSaveEstateField}
        />
      </Grid.Item>
      <Grid.Item span={12}>
        <FieldRow>
          <FieldRowItem>
            <InputFloating
              name="placeOfDeath"
              label="Place of death"
              handleSave={handleSaveEstateField}
            />
          </FieldRowItem>
          <FieldRowItem>
            <InputFloating
              name="occupation"
              label="Occupation"
              handleSave={handleSaveEstateField}
            />
          </FieldRowItem>
        </FieldRow>
      </Grid.Item>
    </Section>
  )
}

const DeceasedForm = withForm(DeceasedFields)

const Deceased = ({
  probateEstate,
  probateCase,
  saveProbateEstateField,
  saveProbateCaseField,
}: Props) => {
  const attrs = probateEstate.attributes
  const dateOfBirthParts = splitIsoDate(attrs.dateOfBirth ?? null)
  const dateOfDeathParts = splitIsoDate(attrs.dateOfDeath ?? null)

  const initialValues = {
    title: attrs.title ?? null,
    firstName: probateCase.attributes.deceasedFirstName ?? null,
    lastName: probateCase.attributes.deceasedLastName ?? null,
    nationalInsuranceNumber: attrs.nationalInsuranceNumber ?? null,
    address: attrs.lastPermanentAddress ?? null,
    dateOfBirth: attrs.dateOfBirth ?? null,
    dateOfBirthDay: dateOfBirthParts.day,
    dateOfBirthMonth: dateOfBirthParts.month,
    dateOfBirthYear: dateOfBirthParts.year,
    dateOfDeath: attrs.dateOfDeath ?? null,
    dateOfDeathDay: dateOfDeathParts.day,
    dateOfDeathMonth: dateOfDeathParts.month,
    dateOfDeathYear: dateOfDeathParts.year,
    placeOfDeath: attrs.placeOfDeath ?? null,
    occupation: attrs.occupation ?? null,
    deceasedNotes: attrs.deceasedNotes ?? null,
  }

  return (
    <DeceasedForm
      initialValues={initialValues}
      validationSchema={DECEASED_SCHEMA}
      saveProbateEstateField={saveProbateEstateField}
      saveProbateCaseField={saveProbateCaseField}
    />
  )
}

export default Deceased
